// Errores de registrar el IMEI y abrir el enrolamiento del candado (paso 09, pista de crédito) →
// algo que el vendedor pueda ACCIONAR con el cliente enfrente.
import { ApiError } from '@/api/errors';
import { wizardErrorMsg } from './api/wizardError';
import { lineErrorMessage } from './lineErrors';

/** Rechazos del candado: el mismo texto en el alta y en el aviso del poll. */
export const MDM_REJECTION_MESSAGES = {
    MDM_DEVICE_REGISTERED_ELSEWHERE:
        'Este equipo ya está dado de alta con el proveedor del candado. Escoge otro equipo o pide a soporte que lo liberen.',
    MDM_DEVICE_MODEL_NOT_SUPPORTED:
        'Este modelo todavía no está habilitado para el candado. Escoge otro equipo o avisa a soporte para que lo den de alta.',
    MDM_PROVIDER_CONFIG_ERROR:
        'No pudimos conectar con el proveedor del candado por un problema de configuración nuestro. Avísale a soporte; no es algo del equipo.',
    MDM_ENROLL_REJECTED:
        'El proveedor rechazó el alta del equipo. Repórtalo a soporte con el IMEI a la mano.',
} as const;

const MESSAGE_BY_CODE: Record<string, string> = {
    INVALID_IMEI: 'El IMEI no es válido — revisa los 15 dígitos del empaque.',
    IMEI_IN_USE:
        'Este IMEI ya está registrado en un crédito. Revisa que sea el equipo correcto o avisa a soporte.',
    ...MDM_REJECTION_MESSAGES,
    // Code del silo anterior a la separación por motivo.
    MDM_ENROLL_FAILED: MDM_REJECTION_MESSAGES.MDM_ENROLL_REJECTED,
    MDM_PROVIDER_UNAVAILABLE:
        'El proveedor del candado no responde en este momento. Vuelve a intentar en un momento.',
    // La reserva dura 30 min: sin ella no hay equipo que entregar y hay que volver a apartarlo.
    RESERVATION_EXPIRED:
        'La reserva del equipo venció, así que no hay unidad apartada para este crédito.',
    CATALOG_UNAVAILABLE: 'El inventario no respondió; vuelve a intentar en un momento.',
    DEVICE_ENROLLMENT_FAILED:
        'El alta del candado de este equipo ya falló y no se puede reintentar desde aquí. Llama a soporte para que la reintenten desde el backoffice.',
    PRODUCT_WITHOUT_MDM: 'Este producto no se vende con candado; revisa el producto en la venta.',
};

/** Rechazos del IMEI elegido: se marcan en el campo y se corrigen escaneando otra pieza. */
const IMEI_BLAMED_CODES = new Set(['IDENTIFIER_MISMATCH', 'IDENTIFIER_NOT_AVAILABLE']);

/** ¿El problema es el IMEI capturado? Entonces reintentar con el mismo no sirve. */
export function deviceErrorBlamesImei(error: unknown): boolean {
    return error instanceof ApiError && IMEI_BLAMED_CODES.has(error.code);
}

// La llamada registra el IMEI y abre el enrolamiento, así que el genérico no nombra ninguno de los
// dos. Re-llamarla con el MISMO IMEI es idempotente.
const FALLBACK = 'No se pudo registrar el equipo — intenta de nuevo en un momento.';

/** Los dos rechazos de cambiar el IMEI: la salida es escanear el equipo ya registrado. */
const IMEI_LOCKED_CODES = new Set(['IMEI_CHANGE_NOT_SUPPORTED', 'DEVICE_ALREADY_ENROLLED']);

/** ¿El IMEI ya no se puede mover? Se usa el registrado o, si está mal, se cancela la venta. */
export function deviceErrorLocksImei(error: unknown): boolean {
    return error instanceof ApiError && IMEI_LOCKED_CODES.has(error.code);
}

const ALREADY_ENROLLED_MESSAGE =
    'El candado ya quedó activo en el equipo de este crédito. Escanea ese mismo equipo; para cambiarlo, repórtalo a soporte.';

/** Últimos 4 dígitos del IMEI registrado (`data.registeredImeiMasked` = `****1234`). */
function registeredImeiTail(error: ApiError): string | undefined {
    const masked = (error.data as { registeredImeiMasked?: unknown } | null | undefined)
        ?.registeredImeiMasked;
    if (typeof masked !== 'string') return undefined;
    const tail = masked.replace(/\D/g, '');
    return tail === '' ? undefined : tail;
}

/** Mensaje de los rechazos de IMEI ya fijado; `undefined` si el code es otro. */
export function lockedImeiMessage(error: unknown): string | undefined {
    if (!(error instanceof ApiError)) return undefined;
    if (error.code === 'DEVICE_ALREADY_ENROLLED') return ALREADY_ENROLLED_MESSAGE;
    if (error.code !== 'IMEI_CHANGE_NOT_SUPPORTED') return undefined;
    const tail = registeredImeiTail(error);
    return tail !== undefined
        ? `Este crédito ya tiene registrado el IMEI que termina en ${tail}. Escanea ese mismo equipo.`
        : 'Este crédito ya tiene registrado otro IMEI. Escanea el equipo que ya quedó registrado.';
}

/** El code del rechazo que el silo adjunta al alta ya fallida (`data.failureCode`). */
function enrollmentFailureCode(error: ApiError): string | undefined {
    if (error.code !== 'DEVICE_ENROLLMENT_FAILED') return undefined;
    const code = (error.data as { failureCode?: unknown } | null | undefined)?.failureCode;
    return typeof code === 'string' ? code : undefined;
}

export function deviceErrorMessage(error: unknown): string {
    const locked = lockedImeiMessage(error);
    if (locked !== undefined) return locked;
    if (error instanceof ApiError) {
        const code = enrollmentFailureCode(error) ?? error.code;
        const known = (MESSAGE_BY_CODE[code] ?? MESSAGE_BY_CODE[error.code]) as string | undefined;
        if (known !== undefined) return known;
    }
    return lineErrorMessage(error) ?? wizardErrorMsg(error, FALLBACK);
}

/** Rechazos que se resuelven cambiando el producto de la venta. */
const PRODUCT_BLAMED_CODES = new Set(['RESERVATION_EXPIRED', 'PRODUCT_WITHOUT_MDM']);

/** ¿La salida es volver al catálogo? Cuando el equipo dejó de estar apartado o no lleva candado. */
export function deviceErrorNeedsProduct(error: unknown): boolean {
    return error instanceof ApiError && PRODUCT_BLAMED_CODES.has(error.code);
}

/** ¿El enrolamiento ya falló en el silo? Reintentar el POST no lo reabre: lo resuelve soporte. */
export function deviceErrorEnrollmentFailed(error: unknown): boolean {
    return error instanceof ApiError && error.code === 'DEVICE_ENROLLMENT_FAILED';
}

/** Rechazos del candado que no cambian con otro intento: el equipo o la configuración mandan. */
const MDM_FINAL_CODES = new Set<string>([
    ...Object.keys(MDM_REJECTION_MESSAGES),
    'MDM_ENROLL_FAILED',
]);

/** ¿Tiene sentido ofrecer reintentar? No cuando el proveedor ya dio un veredicto sobre el equipo. */
export function deviceErrorRetryHelps(error: unknown): boolean {
    return !(error instanceof ApiError && MDM_FINAL_CODES.has(error.code));
}
